import type { Variables } from 'graphql-request';

import { isDefined } from '@e-socna/utils';

type GraphQLErrorShape = Readonly<{
  message: string;
  extensions?: Record<string, unknown>;
}>;

interface GraphQLResponse<TResult> {
  readonly data?: TResult;
  readonly errors?: readonly GraphQLErrorShape[];
}

export class PersistedQueryNotFoundError extends Error {
  constructor() {
    super('PERSISTED_QUERY_NOT_FOUND');
  }
}

class PersistedQueryError extends Error {
  readonly response: Readonly<{ errors: readonly GraphQLErrorShape[] }>;

  constructor(errors: readonly GraphQLErrorShape[]) {
    super(errors.map((e) => e.message).join(' | '));
    this.response = { errors };
  }
}

interface SendOptions {
  readonly endpoint: string;
  // Aborts the request once its result is no longer awaited (a superseded query).
  readonly signal?: AbortSignal;
}

interface Body<V extends Variables | undefined> {
  extensions: { persistedQuery: { version: number; sha256Hash: string } };
  variables?: V;
}

export const sendPersistedQuery = async <
  TResult,
  TVariables extends Variables | undefined,
>(
  hash: string,
  variables: TVariables | undefined,
  { endpoint, signal }: SendOptions,
): Promise<TResult> => {
  const body: Body<TVariables> = {
    extensions: { persistedQuery: { version: 1, sha256Hash: hash } },
  };

  if (isDefined(variables)) {
    body.variables = variables;
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    mode: 'cors',
    signal,
    credentials: 'include',
  });

  const json = (await response.json()) as GraphQLResponse<TResult>;

  if (json.errors?.length) {
    const isNotFound = json.errors.some(
      // eslint-disable-next-line dot-notation
      (e) => e.extensions?.['code'] === 'PERSISTED_QUERY_NOT_FOUND',
    );

    if (isNotFound) {
      throw new PersistedQueryNotFoundError();
    }

    throw new PersistedQueryError(json.errors);
  }

  if (json.data === undefined) {
    throw new Error('No data in GraphQL response');
  }

  return json.data;
};
